import { useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    RenderIf, InternalTooltip, useDefaultTooltipConnector, MenuItem,
} from 'react-rainbow-components';
import { Thread } from 'data/firestore/agent/thread/types';
import { EntityGet } from 'data/firestore/types';
import { formatRelativeDate } from 'data/services/date/formatter';
import getDisplayName from 'data/services/profile/getDisplayName';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import getInitials from 'data/services/string/getInitials';
import removeActionsTokens from 'helpers/removeActionsTokens';
import { Customer } from 'data/firestore/agent/customer/types';
import { Channel, MultimodalPart } from 'data/firestore/globals';
import SaraAvatar from 'components/icons/saraAvatar';
import DotsVertical from 'components/icons/dotsVertical';
import MailOpenedIcon from 'components/icons/mailOpened';
import MailClosedIcon from 'components/icons/mailClosed';
import FlagIcon from 'components/icons/flag';
import useSendFeedback from 'hooks/useSendFeedback';
import AvatarWithChannelIcon from './channelIcon';
import {
    DateLabel,
    NotificationContent,
    NotificationContainer,
    NotificationTitle,
    NotificationText,
    TitleRow,
    PaidLabel,
    LatestOpportunityStageRow,
    MessageRow,
    AIStatusIconWrapper,
    TooltipContent,
    StyledButtonMenu,
    ButtonMenuWrapper,
} from './styled';
import normalizeDate from './helpers/normalizeDate';
import resolveChannel from './helpers/resolveChannel';
import CustomerLatestOpportunityStageBadge from './CustomerLatestOpportunityStageBadge';

interface ThreadItemProps {
    threadData: EntityGet<Thread>;
    isSelected?: boolean;
    onClick?: ((thread: EntityGet<Thread>) => void);
    onUpdateSeenStatus?: (threadId: string, seenStatus: Thread['seenStatus']) => void;
}

const ThreadItem = ({
    threadData,
    isSelected = false,
    onClick = () => {},
    onUpdateSeenStatus,
}: ThreadItemProps) => {
    const { agentId = '' } = useParams<{ agentId: string }>();
    const { sendFeedback } = useSendFeedback();

    const handleToggleSeenStatus = useCallback(
        (e?: React.MouseEvent) => {
            if (e) {
                e.stopPropagation();
            }
            if (threadData?.id && onUpdateSeenStatus) {
                const newStatus = threadData.seenStatus === 'seen' ? 'unseen' : 'seen';
                onUpdateSeenStatus(threadData.id, newStatus);
            }
        },
        [threadData, onUpdateSeenStatus],
    );

    const handleReportThread = useCallback(
        (e?: React.MouseEvent) => {
            if (e) {
                e.stopPropagation();
            }
            if (agentId && threadData?.id) {
                sendFeedback({
                    agentId,
                    context: {
                        type: 'conversation',
                        customerId: threadData.customer?.id || '',
                        channel: threadData.from?.channel as Channel,
                        threadId: threadData.id,
                    },
                });
            }
        },
        [agentId, threadData, sendFeedback],
    );

    const handleMenuWrapperClick = useCallback(
        (e: React.MouseEvent) => {
            // Only stop propagation if clicking on the button itself
            const target = e.target as HTMLElement;
            const button = target.closest('button');
            if (button) {
                e.stopPropagation();
            }
        },
        [],
    );

    const isUnseen = threadData?.seenStatus === 'unseen';
    const menuLabel = isUnseen ? 'Mark as Read' : 'Mark as Unread';
    const menuIcon = isUnseen ? <MailOpenedIcon /> : <MailClosedIcon />;

    const threadDisplayName = useMemo(
        () => getDisplayName(
            threadData?.customer,
            formatIntlPhoneNumber(threadData?.customer?.phoneNumber as string),
        ),
        [threadData],
    );

    const formattedDate = useMemo(
        () => {
            const lastUpdated = threadData?.lastMessage?.createdAt || threadData?.updatedAt;
            if (!lastUpdated) return null;
            const date = normalizeDate(lastUpdated);
            return formatRelativeDate(date);
        },
        [threadData],
    );

    const avatarData = useMemo(() => {
        if (threadData?.customer) {
            return {
                initials: getInitials(threadData.customer as unknown as Record<string, unknown>),
                value: threadData.customer.id,
                src: threadData.customer.photoURL,
            };
        }
        return {
            value: threadData?.id || 'unknown',
        };
    }, [threadData]);

    const latestMessage = useMemo(() => {
        const { content, text } = threadData?.lastMessage || {};

        // Check if content is a multimodal array
        if (Array.isArray(content) && content.length > 0) {
            const lastPart = content[content.length - 1] as MultimodalPart;

            // Handle text part
            if (lastPart.type === 'text') {
                return removeActionsTokens(lastPart.text || '');
            }

            // Handle binary part - determine media type from mimeType
            if (lastPart.type === 'binary') {
                const mimeType = lastPart.mimeType?.toLowerCase() || '';
                if (mimeType.includes('image')) return 'Photo';
                if (mimeType.includes('video')) return 'Video';
                if (mimeType.includes('audio')) return 'Audio';
                return 'Attachment';
            }
        }

        // Fall back to legacy text field
        return removeActionsTokens(text || '');
    }, [threadData?.lastMessage]);

    const threadChannel = resolveChannel({
        channel: threadData?.from?.channel || '',
        customer: threadData?.customer as Customer,
    });

    const isAiActive = threadData?.aiResponseStatus !== 'disabled';
    const containerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef();

    const {
        onMouseEnter,
        onMouseLeave,
        isVisible,
    } = useDefaultTooltipConnector({
        tooltipRef,
        triggerRef: () => containerRef as any,
    });

    const tooltipText = 'Sara is currently not responding to messages';

    return (
        <NotificationContainer
            onClick={() => onClick(threadData)}
            isSelected={isSelected}
            isUnseen={isUnseen}
        >
            <AvatarWithChannelIcon
                initials={avatarData.initials}
                value={avatarData.value}
                src={avatarData.src}
                channel={threadChannel}
                customerChannel={threadData?.customer?.channel}
                size="medium"
                iconSize="small"
            />
            <NotificationContent>
                <TitleRow>
                    <NotificationTitle>
                        <span>{threadDisplayName}</span>
                    </NotificationTitle>
                    <DateLabel>{formattedDate}</DateLabel>
                </TitleRow>
                <MessageRow>
                    <NotificationText>
                        {latestMessage}
                    </NotificationText>
                    <RenderIf isTrue={!isAiActive}>
                        <AIStatusIconWrapper
                            ref={containerRef}
                            onMouseEnter={onMouseEnter}
                            onMouseLeave={onMouseLeave}
                            isActive={isAiActive}
                        >
                            <SaraAvatar />
                        </AIStatusIconWrapper>
                        <InternalTooltip
                            triggerElementRef={() => containerRef}
                            isVisible={isVisible}
                            preferredPosition="bottom"
                            ref={tooltipRef}
                        >
                            <TooltipContent>
                                {tooltipText}
                            </TooltipContent>
                        </InternalTooltip>
                    </RenderIf>
                </MessageRow>
                <LatestOpportunityStageRow>
                    {threadData?.metaAdReferral ? <PaidLabel>Paid</PaidLabel> : null}
                    <CustomerLatestOpportunityStageBadge
                        customerId={threadData?.customer?.id ?? ''}
                    />
                </LatestOpportunityStageRow>
            </NotificationContent>
            <ButtonMenuWrapper onClick={handleMenuWrapperClick}>
                <StyledButtonMenu
                    icon={<DotsVertical />}
                    menuAlignment="right"
                    menuSize="x-small"
                    buttonVariant="base"
                    buttonSize="small"
                >
                    <MenuItem
                        label={menuLabel}
                        onClick={handleToggleSeenStatus}
                        icon={menuIcon}
                    />
                    <MenuItem
                        label="Report Thread"
                        onClick={handleReportThread}
                        icon={<FlagIcon />}
                    />
                </StyledButtonMenu>
            </ButtonMenuWrapper>
        </NotificationContainer>
    );
};

export default ThreadItem;
